/**
 * Microsoft Clarity observability layer.
 *
 * Wraps the window.clarity queue API so the app never calls it directly.
 * Environment-gated: disabled by default in dev/test/E2E, requires an explicit
 * project ID in production builds.
 *
 * Privacy rules enforced here:
 * - No raw emails, passwords, tokens, query params, or request bodies.
 * - Endpoint paths are normalized (/api/fees/:id, not /api/fees/42).
 * - Duration values are bucketed, never raw.
 * - User identification uses internal non-PII IDs only.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClarityConfig {
  projectId: string;
  appName: string;
  appEnv: 'development' | 'staging' | 'production';
  appVersion?: string;
  releaseSha?: string;
}

export interface ClarityConsentState {
  analyticsStorage: 'granted' | 'denied';
}

export interface RouteContext {
  path: string;
  pageType: 'public' | 'authenticated' | 'admin' | 'checkout';
  routeGroup?: string;
}

export interface UserContext {
  userId?: string;
  role?: string;
  plan?: string;
  authState: 'authenticated' | 'anonymous' | 'expired';
  tenantType?: string;
  onboardingState?: 'not-started' | 'in-progress' | 'completed' | 'dismissed';
}

export interface ApiFailureContext {
  method: string;
  endpoint: string;
  statusCode?: number;
  statusClass?: '4xx' | '5xx' | 'network' | 'timeout';
  errorCode?: string;
  routeId?: string;
  journeyId?: string;
  journeyStep?: string;
  retryAttempted?: boolean;
  retrySucceeded?: boolean;
  durationBucket?: DurationBucket;
}

export interface UiErrorContext {
  errorType: string;
  errorArea?: string;
  routeId?: string;
  journeyId?: string;
  journeyStep?: string;
}

export interface PerformanceSignal {
  name: string;
  bucket: DurationBucket;
  routeId?: string;
  journeyId?: string;
}

export interface JourneyStepContext {
  journeyId: string;
  stepId: string;
  durationBucket?: DurationBucket;
  metadata?: Record<string, string>;
}

export type DurationBucket =
  | 'lt_1s'
  | '1_3s'
  | '3_10s'
  | '10_30s'
  | '30_60s'
  | 'gt_60s';

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let _initialized = false;
let _enabled = false;
let _projectId = '';

// ---------------------------------------------------------------------------
// window.clarity type declaration
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    clarity?: {
      (...args: ['event', string]): void;
      (...args: ['set', string, string | string[]]): void;
      (...args: ['identify', string, string?, string?, string?]): void;
      (...args: ['consent', { ad_storage?: 'granted' | 'denied'; analytics_storage?: 'granted' | 'denied' }]): void;
      q?: unknown[][];
    };
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function shouldEnable(): boolean {
  const envVar = import.meta.env.VITE_ENABLE_CLARITY;
  if (envVar === 'true' || envVar === '1') return true;
  if (envVar === 'false' || envVar === '0') return false;

  // Never run in E2E test builds
  if (import.meta.env.VITE_E2E === '1') return false;

  // Default: disabled in dev/test, enabled in prod with a project ID
  if (import.meta.env.DEV || import.meta.env.MODE === 'test') return false;
  return !!import.meta.env.VITE_CLARITY_PROJECT_ID;
}

export function normalizeEndpoint(url: string): string {
  try {
    const u = new URL(url, 'http://x');
    return u.pathname
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:uuid')
      .replace(/\/\d+/g, '/:id');
  } catch {
    return url
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:uuid')
      .replace(/\/\d+/g, '/:id');
  }
}

export function bucketDuration(ms: number): DurationBucket {
  if (ms < 1000) return 'lt_1s';
  if (ms < 3000) return '1_3s';
  if (ms < 10000) return '3_10s';
  if (ms < 30000) return '10_30s';
  if (ms < 60000) return '30_60s';
  return 'gt_60s';
}

function safeVal(val: unknown, max = 80): string {
  const s = String(val ?? '').trim();
  if (!s) return 'unknown';
  return s.length > max ? s.slice(0, max) + '…' : s;
}

function safeTags(record: Record<string, string | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(record)) {
    if (v !== undefined && v !== '') out[k] = v;
  }
  return out;
}

function clarity(): Window['clarity'] | undefined {
  return window.clarity;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initClarity(config: ClarityConfig): void {
  if (_initialized) return;
  _initialized = true;
  _enabled = shouldEnable();
  _projectId = config.projectId;

  if (!_enabled || !_projectId) return;

  // Queue function must exist before the script tag loads
  const c = window.clarity;
  if (!c) {
    (window as unknown as Record<string, unknown>).clarity = function (...args: unknown[]) {
      ((window.clarity as { q?: unknown[][] }).q = (window.clarity as { q?: unknown[][] }).q ?? []).push(args);
    };
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${_projectId}`;
  script.onload = () => {
    setClarityTags({
      app_name: config.appName,
      app_env: config.appEnv,
      app_version: config.appVersion,
      release_sha: config.releaseSha,
    });
  };
  document.head.appendChild(script);
}

export function isClarityEnabled(): boolean {
  return _enabled && _initialized;
}

/** Force-enable Clarity for integration tests only. */
export function __forceEnableForTests(): void {
  _initialized = true;
  _enabled = true;
  _projectId = 'TEST_MOCK';
}

export function setClarityConsent(state: ClarityConsentState): void {
  if (!isClarityEnabled()) return;
  clarity()?.('consent', {
    analytics_storage: state.analyticsStorage,
    ad_storage: 'denied',
  });
}

export function identifyClarityUser(ctx: UserContext): void {
  if (!isClarityEnabled()) return;
  setClarityTags(safeTags({
    auth_state: ctx.authState,
    user_role: ctx.role,
    plan: ctx.plan,
    tenant_type: ctx.tenantType,
    onboarding_state: ctx.onboardingState,
  }));
  if (ctx.userId) {
    clarity()?.('identify', ctx.userId);
  }
}

export function clearClarityUser(): void {
  if (!isClarityEnabled()) return;
  setClarityTags({
    auth_state: 'anonymous',
    user_role: '',
    plan: '',
    tenant_type: '',
    onboarding_state: '',
  });
  // Clarity does not support "unidentify" — the session carries the last
  // identify. We clear identity tags only; session tags persist.
}

export function setClarityTags(tags: Record<string, string | undefined>): void {
  if (!isClarityEnabled()) return;
  for (const [key, value] of Object.entries(tags)) {
    if (value !== undefined && value !== '') {
      clarity()?.('set', key, value);
    }
  }
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export function trackClarityEvent(name: string, tags?: Record<string, string>): void {
  if (!isClarityEnabled()) return;
  clarity()?.('event', name);
  if (tags) setClarityTags(tags);
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export function trackRouteView(ctx: RouteContext): void {
  if (!isClarityEnabled()) return;
  setClarityTags({
    route_id: safeVal(ctx.path),
    route_group: ctx.routeGroup,
    page_type: ctx.pageType,
  });
  clarity()?.('event', 'route-viewed');
}

// ---------------------------------------------------------------------------
// Journeys
// ---------------------------------------------------------------------------

export function trackJourneyStart(journeyId: string, metadata?: Record<string, string>): void {
  if (!isClarityEnabled()) return;
  setClarityTags({ journey_id: journeyId, journey_step: 'start', journey_status: 'in-progress' });
  clarity()?.('event', 'journey-started');
  if (metadata) setClarityTags(metadata);
}

export function trackJourneyStep(journeyId: string, stepId: string, metadata?: Record<string, string>): void {
  if (!isClarityEnabled()) return;
  setClarityTags({ journey_id: journeyId, journey_step: stepId, journey_status: 'in-progress' });
  clarity()?.('event', 'journey-step-viewed');
  if (metadata) setClarityTags(metadata);
}

export function trackJourneyComplete(journeyId: string, metadata?: Record<string, string>): void {
  if (!isClarityEnabled()) return;
  setClarityTags({ journey_id: journeyId, journey_status: 'completed' });
  clarity()?.('event', 'journey-completed');
  if (metadata) setClarityTags(metadata);
}

export function trackJourneyAbandon(journeyId: string, metadata?: Record<string, string>): void {
  if (!isClarityEnabled()) return;
  setClarityTags(safeTags({
    journey_id: journeyId,
    journey_status: 'abandoned',
    ...metadata,
  }));
  clarity()?.('event', 'journey-abandoned');
}

export function trackJourneyFailure(journeyId: string, failureReason?: string, metadata?: Record<string, string>): void {
  if (!isClarityEnabled()) return;
  setClarityTags(safeTags({
    journey_id: journeyId,
    journey_status: 'failed',
    last_error_type: failureReason,
    ...metadata,
  }));
  clarity()?.('event', 'journey-failed');
}

// ---------------------------------------------------------------------------
// API / Network
// ---------------------------------------------------------------------------

export function trackApiFailure(ctx: ApiFailureContext): void {
  if (!isClarityEnabled()) return;
  setClarityTags(safeTags({
    last_error_type: 'api-failure',
    last_error_area: normalizeEndpoint(ctx.endpoint),
    api_health_bucket: ctx.statusClass,
  }));
  clarity()?.('event', 'api-request-failed');

  if (ctx.statusCode === 401 || ctx.errorCode === 'UNAUTHENTICATED') {
    clarity()?.('event', 'api-unauthorized');
  } else if (ctx.statusCode === 403 || ctx.errorCode === 'FORBIDDEN') {
    clarity()?.('event', 'api-forbidden');
  } else if (ctx.statusCode === 429) {
    clarity()?.('event', 'api-rate-limited');
  } else if (ctx.statusCode && ctx.statusCode >= 500) {
    clarity()?.('event', 'api-server-error');
  }

  if (ctx.retrySucceeded) {
    clarity()?.('event', 'api-retry-succeeded');
  }
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export function trackUiError(ctx: UiErrorContext): void {
  if (!isClarityEnabled()) return;
  setClarityTags(safeTags({
    last_error_type: ctx.errorType,
    last_error_area: ctx.errorArea,
    journey_id: ctx.journeyId,
    journey_step: ctx.journeyStep,
    route_id: ctx.routeId,
  }));
  clarity()?.('event', 'ui-error-boundary-hit');
}

export function trackUncaughtError(errorType: string): void {
  if (!isClarityEnabled()) return;
  setClarityTags({ last_error_type: errorType, last_error_area: 'uncaught' });
  clarity()?.('event', 'uncaught-error');
}

export function trackUnhandledRejection(): void {
  if (!isClarityEnabled()) return;
  setClarityTags({ last_error_type: 'unhandled-rejection', last_error_area: 'uncaught' });
  clarity()?.('event', 'unhandled-promise-rejection');
}

// ---------------------------------------------------------------------------
// Performance
// ---------------------------------------------------------------------------

export function trackPerformanceSignal(signal: PerformanceSignal): void {
  if (!isClarityEnabled()) return;
  setClarityTags(safeTags({
    performance_bucket: signal.bucket,
    route_id: signal.routeId,
    journey_id: signal.journeyId,
  }));
  clarity()?.('event', `performance-${signal.name}`);
}

// ---------------------------------------------------------------------------
// Auth events (convenience)
// ---------------------------------------------------------------------------

export function trackAuthEvent(eventName: string): void {
  if (!isClarityEnabled()) return;
  clarity()?.('event', eventName);
}

// ---------------------------------------------------------------------------
// Paywall / billing events (convenience)
// ---------------------------------------------------------------------------

export function trackBillingEvent(eventName: string): void {
  if (!isClarityEnabled()) return;
  clarity()?.('event', eventName);
}

// ---------------------------------------------------------------------------
// Onboarding events (convenience)
// ---------------------------------------------------------------------------

export function trackOnboardingEvent(eventName: string, step?: string): void {
  if (!isClarityEnabled()) return;
  if (step) setClarityTags({ onboarding_step: step });
  clarity()?.('event', eventName);
}

// ---------------------------------------------------------------------------
// Navigation events (convenience)
// ---------------------------------------------------------------------------

export function trackNavEvent(eventName: string, target?: string): void {
  if (!isClarityEnabled()) return;
  if (target) setClarityTags({ nav_target: safeVal(target) });
  clarity()?.('event', eventName);
}
